// apps/landing-page/src/components/ui/CoverPlaceholder.tsx
/**
 * Branded fallback poster for product cards without a cover image.
 * Mirrors the seeded SVG poster look (gradient, soft circles, category
 * label, wordmark) but renders live from ExamType.color so it never
 * drifts from the accent color used elsewhere on the same card.
 */
interface CoverPlaceholderProps {
  /** Big poster text — exam-type name when available, else the product title. */
  label: string;
  /** ExamType.color hex; falls back to brand teal. */
  accentColor?: string | null;
  /** Smaller typography for compact/thumbnail cards. */
  compact?: boolean;
  className?: string;
}

const BRAND_TEAL = '#0d9488';

export function CoverPlaceholder({ label, accentColor, compact = false, className = '' }: CoverPlaceholderProps) {
  const accent = accentColor || BRAND_TEAL;
  return (
    <div
      aria-hidden
      className={`relative h-full w-full select-none overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 70%, #0f172a) 0%, ${accent} 60%, color-mix(in srgb, ${accent} 78%, #ffffff) 100%)`,
      }}
    >
      <div className="absolute -right-8 -top-10 h-40 w-40 rounded-full bg-white/10" />
      <div className="absolute -bottom-12 right-14 h-32 w-32 rounded-full bg-slate-900/10" />
      <div className="absolute left-1/2 top-1/4 h-14 w-14 rounded-full bg-white/10" />
      <span
        className={`absolute font-bold text-white/90 ${compact ? 'left-2.5 top-2 text-[9px]' : 'left-4 top-3 text-[11px]'}`}
      >
        Aksa<span className="text-white/60">demia</span>
      </span>
      <span
        className={`absolute font-bold leading-tight text-white drop-shadow-sm ${
          compact ? 'inset-x-2.5 bottom-2 line-clamp-2 text-sm' : 'inset-x-4 bottom-3.5 line-clamp-2 text-2xl'
        }`}
        style={{ fontFamily: 'var(--font-display, Outfit, sans-serif)' }}
      >
        {label}
      </span>
    </div>
  );
}
